import React, {
  useCallback,
  useEffect,
  useState,
  type KeyboardEvent,
  type ChangeEvent,
  useMemo,
  useRef,
} from "react";
import { debounce } from "lodash-es";
import { constrainValue } from "utils";
import { InputComponent } from "./InputComponent";

type NumberInputSaveOnChangeProps = {
  initialValue: number;
  onSave: (n: number) => void;
  min?: number;
  max: number;
  disabled: boolean;
  classNames: string;
  testId: string;
};
export const NumberInputSaveOnChange = ({
  initialValue,
  onSave,
  min = 0,
  max,
  disabled,
  classNames,
}: NumberInputSaveOnChangeProps) => {
  const [inputValue, setInputValue] = useState(initialValue || min);

  const handleMinusClick = useCallback(() => {
    setInputValue((val) => constrainValue(+val - 1, min, max));
  }, [max, min]);

  const handlePlusClick = useCallback(() => {
    setInputValue((val) => constrainValue(+val + 1, min, max));
  }, [max, min]);

  const handleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const newValue = constrainValue(parseFloat(e.target.value), min, max);
      setInputValue(newValue);
    },
    [min, max],
  );

  const onSaveRef = useRef(onSave);
  useEffect(() => {
    onSaveRef.current = onSave;
  }, [onSave]);

  const debounceOnSave = useMemo(
    // eslint-disable-next-line react-hooks/refs
    () => debounce((value) => onSaveRef.current(value), 200),
    [],
  );

  useEffect(() => {
    debounceOnSave(inputValue);
    return () => debounceOnSave.cancel();
  }, [inputValue, max, min, debounceOnSave]);

  return (
    <InputComponent
      classNames={classNames}
      value={inputValue}
      min={min}
      max={max}
      disabled={disabled}
      handleMinusClick={handleMinusClick}
      handlePlusClick={handlePlusClick}
      handleChange={handleChange}
      onEnterPressed={(e: KeyboardEvent<HTMLDivElement>) =>
        e.currentTarget.blur()
      }
    />
  );
};
